/**
 * studentAttendanceService.js — Dedicated Firestore service for Student Attendance.
 * 
 * Unlike employee/teacher attendance which tracks shifts, late arrivals, and early departures,
 * student attendance is strictly binary:
 *   - PRESENT
 *   - ABSENT
 * 
 * Collections:
 *   - 'student_attendance': Class-level daily records (${TENANT_ID}_${className}_${section}_${date})
 *   - 'student_rfid_logs': Individual student daily punch records (${TENANT_ID}_${studentId}_${date})
 */

import {
  doc, getDoc, setDoc, serverTimestamp, where,
} from 'firebase/firestore';
import { db, isFirebaseConfigured } from '../../lib/firebase';
import { safe, queryDocs, invalidateCache } from './firestoreHelpers';
import { getBiometricLogsForDate } from './biometricService';
import { saveAttendance } from './attendanceService';

const TENANT_ID = process.env.REACT_APP_TENANT_ID || 'stpauls';
const COL = 'student_attendance';
const LOGS_COL = 'student_rfid_logs';

const studentAttCache = new Map();
const CACHE_TTL = 3 * 60 * 1000;

function guard() { return isFirebaseConfigured && !!db; }

function cleanRfid(val) {
  return String(val || '').trim().replace(/^0+/, '');
}

/**
 * Get student attendance for a class-section on a specific date.
 */
export async function getStudentAttendance(className, section, date) {
  if (!guard()) return { records: {}, rfidLogs: {}, present: 0, absent: 0, total: 0 };
  const docId = `${TENANT_ID}_${className}_${section}_${date}`;
  return safe(async () => {
    const snap = await getDoc(doc(db, COL, docId));
    if (!snap.exists()) {
      return { records: {}, rfidLogs: {}, present: 0, absent: 0, total: 0, exists: false };
    }
    const data = snap.data();
    return {
      records: data.records || {},
      rfidLogs: data.rfidLogs || {},
      present: data.present || 0,
      absent: data.absent || 0,
      total: data.total || 0,
      markedBy: data.markedBy || '',
      updatedAt: data.updatedAt || null,
      exists: true,
    };
  }, { records: {}, rfidLogs: {}, present: 0, absent: 0, total: 0, exists: false });
}

/**
 * Save student attendance for a class-section on a date.
 * Validates that records only contain 'PRESENT' or 'ABSENT'.
 * 
 * @param {string} className
 * @param {string} section
 * @param {string} date - 'YYYY-MM-DD'
 * @param {Object} records - { [studentId]: 'PRESENT' | 'ABSENT' }
 * @param {Object} rfidLogs - Optional map of studentId -> { rfid, punchTime, source }
 * @param {string} markedBy - Who marked it
 */
export async function saveStudentAttendance(className, section, date, records, rfidLogs = {}, markedBy = 'Admin') {
  if (!guard()) throw new Error('Firebase is not configured. Cannot save student attendance.');

  // Validate and enforce student attendance states: strictly PRESENT or ABSENT
  const cleanRecords = {};
  for (const [sId, status] of Object.entries(records)) {
    cleanRecords[sId] = status === 'PRESENT' ? 'PRESENT' : 'ABSENT';
  }

  const present = Object.values(cleanRecords).filter((s) => s === 'PRESENT').length;
  const absent  = Object.values(cleanRecords).filter((s) => s === 'ABSENT').length;
  const total   = Object.keys(cleanRecords).length;

  const docId = `${TENANT_ID}_${className}_${section}_${date}`;

  const payload = {
    tenantId: TENANT_ID,
    className,
    section,
    date,
    records: cleanRecords,
    rfidLogs,
    present,
    absent,
    total,
    markedBy,
    updatedAt: serverTimestamp(),
  };

  // 1. Save to dedicated student_attendance collection
  await setDoc(doc(db, COL, docId), payload, { merge: true });

  // 2. Keep legacy 'attendance' collection synced for shared summary views
  try {
    await saveAttendance(className, section, date, cleanRecords, markedBy);
  } catch (err) {
    console.warn('[studentAttendanceService] legacy sync notice:', err.message);
  }

  studentAttCache.clear();
  invalidateCache(COL);

  return { success: true, present, absent, total };
}

/**
 * Record a student RFID punch (e.g. from USB RFID scanner, tap station, or gate reader).
 * 
 * @param {string} rfid - The scanned RFID card number
 * @param {Array} students - List of active students to find the match
 * @param {string} date - Optional 'YYYY-MM-DD' (defaults to today)
 * @param {string} timestamp - Optional 'HH:MM:SS' or ISO string
 * @param {string} source - 'RFID_Scanner' | 'RFID_Tap' | 'Biometric'
 */
export async function recordStudentRfidPunch(rfid, students, date, timestamp, source = 'RFID_Tap') {
  if (!rfid) throw new Error('RFID is required');
  const targetDate = date || new Date().toISOString().slice(0, 10);
  const now = new Date();
  const timeStr = timestamp || now.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true });

  const cRfid = cleanRfid(rfid);

  // Find student by RFID
  const student = students.find((s) => {
    const s1 = cleanRfid(s.rfid);
    const s2 = cleanRfid(s.rfidNo);
    return (s1 && s1 === cRfid) || (s2 && s2 === cRfid);
  });

  if (!student) {
    return {
      success: false,
      found: false,
      error: `Unregistered RFID: ${rfid}`,
      rfid,
    };
  }

  // Record individual log in student_rfid_logs
  const logId = `${TENANT_ID}_${student.id}_${targetDate}`;
  const logPayload = {
    tenantId: TENANT_ID,
    studentId: student.id,
    admissionNo: student.admissionNo || '',
    studentName: student.fullName || '',
    className: student.className || '',
    section: student.section || 'A',
    rfid: cRfid,
    date: targetDate,
    punchTime: timeStr,
    status: 'PRESENT',
    source,
    recordedAt: serverTimestamp(),
  };

  await setDoc(doc(db, LOGS_COL, logId), logPayload, { merge: true });

  // Update the class attendance document
  const className = student.className || 'Unknown';
  const section = student.section || 'A';
  const currentAtt = await getStudentAttendance(className, section, targetDate);

  const updatedRecords = { ...(currentAtt.records || {}) };
  // Mark student present
  updatedRecords[student.id] = 'PRESENT';

  // For students of this class that haven't been marked yet, ensure they exist in records
  const classStudents = students.filter(s => s.className === className && s.section === section);
  classStudents.forEach(s => {
    if (!updatedRecords[s.id]) {
      updatedRecords[s.id] = 'ABSENT'; // Default to ABSENT until scanned
    }
  });

  const updatedRfidLogs = { ...(currentAtt.rfidLogs || {}) };
  updatedRfidLogs[student.id] = {
    rfid: cRfid,
    punchTime: timeStr,
    source,
  };

  await saveStudentAttendance(className, section, targetDate, updatedRecords, updatedRfidLogs, source);

  return {
    success: true,
    found: true,
    student,
    date: targetDate,
    punchTime: timeStr,
    status: 'PRESENT',
  };
}

/**
 * Process biometric logs for students for a given date.
 * Matches punches in 'biometric_logs' with students' RFID.
 * Any student with a punch is marked PRESENT.
 * Any student in the selected/all classes without a punch is marked ABSENT.
 * 
 * @param {string} date - 'YYYY-MM-DD'
 * @param {Array} students - All active students
 * @param {string} targetClass - Optional filter for a specific class (or all)
 * @param {string} targetSection - Optional section filter
 */
export async function processBiometricLogsForStudents(date, students, targetClass = '', targetSection = '') {
  const logs = await getBiometricLogsForDate(date);
  
  // Index logs by clean RFID
  const punchedRfids = new Set();
  const rfidPunchTimes = {};

  for (const log of logs) {
    const candidateCodes = [
      cleanRfid(log.empCode),
      cleanRfid(log.rfid),
      (/^\d+$/.test(String(log.empName || '').trim())) ? cleanRfid(log.empName) : null
    ].filter(c => c && c !== 'N/A');

    for (const code of candidateCodes) {
      punchedRfids.add(code);
      if (!rfidPunchTimes[code]) {
        // Store first punch time
        const timePart = String(log.timestamp || '').split(' ')[1] || '';
        rfidPunchTimes[code] = timePart || 'Recorded';
      }
    }
  }

  // Filter students
  const filteredStudents = students.filter(s => {
    if (targetClass && s.className !== targetClass) return false;
    if (targetSection && s.section !== targetSection) return false;
    return true;
  });

  // Group by className and section
  const classGroups = {};
  for (const s of filteredStudents) {
    const key = `${s.className || 'Unknown'}_${s.section || 'A'}`;
    if (!classGroups[key]) {
      classGroups[key] = {
        className: s.className || 'Unknown',
        section: s.section || 'A',
        students: [],
      };
    }
    classGroups[key].students.push(s);
  }

  let totalPresent = 0;
  let totalAbsent = 0;
  let classesCount = 0;

  for (const group of Object.values(classGroups)) {
    const records = {};
    const rfidLogs = {};

    for (const student of group.students) {
      const sRfid1 = cleanRfid(student.rfid);
      const sRfid2 = cleanRfid(student.rfidNo);
      const hasPunched = (sRfid1 && punchedRfids.has(sRfid1)) || (sRfid2 && punchedRfids.has(sRfid2));

      if (hasPunched) {
        records[student.id] = 'PRESENT';
        const matchedRfid = (sRfid1 && punchedRfids.has(sRfid1)) ? sRfid1 : sRfid2;
        rfidLogs[student.id] = {
          rfid: matchedRfid,
          punchTime: rfidPunchTimes[matchedRfid] || 'Biometric',
          source: 'Biometric_Machine',
        };
        totalPresent++;
      } else {
        records[student.id] = 'ABSENT';
        totalAbsent++;
      }
    }

    await saveStudentAttendance(
      group.className,
      group.section,
      date,
      records,
      rfidLogs,
      'Biometric_Machine_Sync'
    );
    classesCount++;
  }

  return {
    success: true,
    totalLogs: logs.length,
    classesProcessed: classesCount,
    totalStudents: filteredStudents.length,
    totalPresent,
    totalAbsent,
  };
}

/**
 * List student attendance records for a specific date or class.
 */
export async function listStudentAttendance(args = {}) {
  const { className, date } = args;
  const cacheKey = 'list_' + JSON.stringify(args);
  if (studentAttCache.has(cacheKey)) {
    const { data, timestamp } = studentAttCache.get(cacheKey);
    if (Date.now() - timestamp < CACHE_TTL) return data;
  }
  if (!guard()) return [];
  return safe(async () => {
    const constraints = [where('tenantId', '==', TENANT_ID)];
    if (className) constraints.push(where('className', '==', className));
    if (date) constraints.push(where('date', '==', date));
    
    const list = await queryDocs(COL, ...constraints);
    studentAttCache.set(cacheKey, { data: list, timestamp: Date.now() });
    return list;
  }, []);
}

/**
 * Get individual student attendance summary (Present, Absent, Total, Percentage).
 */
export async function getStudentDailySummary(studentId) {
  if (!guard()) return { present: 0, absent: 0, total: 0, pct: 0 };
  return safe(async () => {
    const list = await queryDocs(
      COL,
      where('tenantId', '==', TENANT_ID),
      where(`records.${studentId}`, 'in', ['PRESENT', 'ABSENT'])
    );

    let present = 0, absent = 0;
    list.forEach((docData) => {
      const status = (docData.records || {})[studentId];
      if (status === 'PRESENT') present++;
      else if (status === 'ABSENT') absent++;
    });

    const total = present + absent;
    return {
      present,
      absent,
      total,
      pct: total ? Math.round((present / total) * 100) : 0,
    };
  }, { present: 0, absent: 0, total: 0, pct: 0 });
}
